// src/hooks/useNLQuery.ts
// Hook for executing natural language queries against the analytics API.
// Supports multi-turn conversations via session_id.

import { useCallback, useRef, useState } from "react";
import { fetchData, createData, deleteData } from "api/apiHelpers";
import { store } from "../redux/store";
import logger from "utils/logger";

/** Visualization metadata returned by the agentic response formatter */
export interface VisualizationMeta {
  type: "bar" | "line" | "pie" | "scatter" | "grouped_bar" | "metric_card" | "table";
  x_axis: string | null;
  y_axis: string | null;
  series: string | null;
}

/** Intent classification from the agentic query pipeline */
export interface IntentMeta {
  intent: "count" | "list" | "distribution" | "trend" | "comparison" | "correlation" | "summary" | "survival";
  x_axis: string | null;
  y_axis: string | null;
  group_by: string | null;
  time_field: string | null;
  aggregation: string | null;
  filters: string[];
  confidence: number;
}

export interface NLQueryResult {
  question: string;
  explanation: string;
  columns: string[];
  rows: Record<string, unknown>[];
  row_count: number;
  tables_used: string[];
  /** True when results were capped at max_rows */
  truncated?: boolean;
  /** The cap applied (e.g. 500) */
  max_rows?: number;
  /** True when this result was served from cache (A2) */
  cached?: boolean;
  /** ISO timestamp of when the cached result was originally computed */
  cached_at?: string;
  /** Raw SQL query (for debugging / transparency) */
  sql?: string;
  /** Classified intent metadata from the agentic layer */
  intent?: IntentMeta;
  /** Visualization recommendation from the response formatter */
  visualization?: VisualizationMeta;
  /** Aggregate statistics for the result set (used for narrative generation) */
  summary_stats?: Record<string, unknown>;
  /** Session ID for multi-turn conversations */
  session_id?: string;
  /** Suggested follow-up queries based on result shape */
  suggested_followups?: string[];
  /** Mode B: "analysis_report" when this is a multi-finding report */
  type?: "analysis_report";
  /** Mode B: report title */
  title?: string;
  /** Mode B: executive summary narrative */
  narrative?: string;
  /** Mode B: structured findings with per-finding charts */
  findings?: AnalysisReportFinding[];
  /** Mode B: analysis metadata */
  metadata?: AnalysisMetadata;
  /** Disambiguation (feature 2/3): which pipeline path produced this result.
   *  "ambiguous" => render `options`; "selected"/"ambiguous_autoselected" => a chosen reading. */
  decision?: string;
  /** Disambiguation: the interpretation options to choose from (when decision === "ambiguous"). */
  options?: NLQueryOption[];
  /** Disambiguation: the judge's one-line rationale for the suggested default. */
  judge_rationale?: string;
  /** Generate-N: agreement confidence and how many distinct candidates were returned. */
  confidence?: number;
  candidates_returned?: number;
  /** A chosen / auto-selected reading carries its own interpretation identity. */
  interpretation_id?: string;
  label?: string;
  interpretation_explanation?: string;
  /** Fallback (UI off): how many alternative readings were collapsed into this auto-pick. */
  alternatives_considered?: number;
}

/** One interpretation in an ambiguous response: a full result PLUS its disambiguation metadata. */
export interface NLQueryOption extends NLQueryResult {
  interpretation_id: string;
  label: string;
  interpretation_explanation: string;
  completeness: "high" | "medium" | "low" | "";
  suggested: boolean;
}

/** Multi-turn disambiguation directive sent back to the pipeline. */
export type NLQueryDirective =
  | { op: "select"; interpretation_id: string }
  | { op: "refine"; instruction: string };

export interface AnalysisReportFinding {
  title: string;
  description: string;
  significance: "high" | "medium" | "low";
  strategy: string;
  visualization: VisualizationMeta;
  rows: Record<string, unknown>[];
  columns: string[];
}

export interface AnalysisMetadata {
  queries_executed: number;
  llm_calls: number;
  elapsed_seconds: number;
  total_rows_analyzed: number;
  budget_remaining: {
    queries: number;
    llm_calls: number;
  };
  partial: boolean;
}

export function isAnalysisReport(result: NLQueryResult): boolean {
  return result.type === "analysis_report";
}

export interface NLHistoryEntry {
  id: number;
  question: string;
  explanation: string;
  tables_used: string[];
  result_count: number;
  created_at: string;
}

interface NLQueryState {
  loading: boolean;
  conversation: NLQueryResult[];
  error: string | null;
  history: NLHistoryEntry[];
  historyLoading: boolean;
  /** Current pipeline step code while a streamed query runs (null when idle). */
  progressStep: string | null;
}

/** Outcome of the streaming endpoint: a result, an error, a Mode-B redirect, or null (unusable). */
type StreamOutcome =
  | { result: NLQueryResult }
  | { error: string }
  | { modeB: true }
  | null;

/**
 * Consume the SSE stream from /api/insights/nl-query/stream/. Calls onStep(code) for each curated
 * progress frame and resolves with the final outcome. Returns null when streaming is unusable
 * (network error, no ReadableStream) so the caller can fall back to the plain POST endpoint.
 * Privacy: the wire only carries step CODES + the same result payload the POST endpoint returns.
 */
async function streamNLQuery(
  body: Record<string, unknown>,
  onStep: (code: string) => void,
): Promise<StreamOutcome> {
  let token: string | undefined;
  try {
    token = store.getState().auth.token || undefined;
  } catch {
    token = undefined;
  }

  let resp: Response;
  try {
    resp = await fetch("/api/insights/nl-query/stream/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch {
    return null; // network/CORS error → fall back to POST
  }

  if (!resp.ok || !resp.body) {
    try {
      const j = await resp.json();
      if (j && j.error) return { error: j.error as string };
    } catch {
      /* not JSON */
    }
    return null;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let outcome: StreamOutcome = null;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() || "";
    for (const part of parts) {
      const dataLine = part.split("\n").find((l) => l.startsWith("data:"));
      if (!dataLine) continue;
      let evt: { type?: string; code?: string; result?: NLQueryResult; error?: string };
      try {
        evt = JSON.parse(dataLine.slice(5).trim());
      } catch {
        continue;
      }
      if (evt.type === "step" && evt.code) onStep(evt.code);
      else if (evt.type === "result" && evt.result) outcome = { result: evt.result };
      else if (evt.type === "mode_b") outcome = { modeB: true };
      else if (evt.type === "error") outcome = { error: evt.error || "Query failed." };
    }
  }
  return outcome;
}

const useNLQuery = () => {
  const [state, setState] = useState<NLQueryState>({
    loading: false,
    conversation: [],
    error: null,
    history: [],
    historyLoading: false,
    progressStep: null,
  });

  const sessionRef = useRef<string | undefined>(undefined);

  const acceptResult = useCallback((data: NLQueryResult & { error?: string }) => {
    if (data.error) {
      setState((prev) => ({ ...prev, loading: false, progressStep: null, error: data.error || "Query failed." }));
      return;
    }
    if (data.session_id) {
      sessionRef.current = data.session_id;
    }
    setState((prev) => ({
      ...prev,
      loading: false,
      progressStep: null,
      conversation: [...prev.conversation, data],
    }));
  }, []);

  const queryFn = useCallback(async (question: string, environmentIds?: number[], directive?: NLQueryDirective) => {
    setState((prev) => ({ ...prev, loading: true, error: null, progressStep: "understanding" }));
    const body: { question: string; environment_ids?: number[]; session_id?: string; directive?: NLQueryDirective } = { question };
    if (environmentIds && environmentIds.length > 0) {
      body.environment_ids = environmentIds;
    }
    if (sessionRef.current) {
      body.session_id = sessionRef.current;
    }
    if (directive) {
      body.directive = directive;
    }

    // Streaming first: emit curated step "thoughts" so the user doesn't wait on a blank screen.
    // Falls back to the plain POST on Mode B (its own flow), stream failure, or no-stream support.
    try {
      const streamed = await streamNLQuery(body as Record<string, unknown>, (code) =>
        setState((prev) => ({ ...prev, progressStep: code })),
      );
      if (streamed && "result" in streamed) {
        acceptResult(streamed.result);
        return;
      }
      if (streamed && "error" in streamed) {
        setState((prev) => ({ ...prev, loading: false, progressStep: null, error: streamed.error }));
        return;
      }
      // streamed === null (unusable) or { modeB:true } → fall through to the POST path.
    } catch (e) {
      logger.error("NL stream failed, falling back to POST:", e);
    }

    setState((prev) => ({ ...prev, progressStep: null }));
    try {
      const data = await createData<typeof body, NLQueryResult & { error?: string }>(
        "/insights/nl-query/",
        body
      );
      acceptResult(data);
    } catch (err: unknown) {
      let message = "Failed to process your question.";
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const axiosErr = err as any;
      if (axiosErr?.response?.data?.error) {
        message = axiosErr.response.data.error;
      } else if (axiosErr?.response?.data?.message) {
        message = axiosErr.response.data.message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      const status = axiosErr?.response?.status;
      logger.error(`NL query failed (HTTP ${status}):`, message, err);
      setState((prev) => ({
        ...prev,
        loading: false,
        progressStep: null,
        error: message,
      }));
    }
  }, [acceptResult]);

  const fetchHistory = useCallback(async () => {
    setState((prev) => ({ ...prev, historyLoading: true }));
    try {
      const data = await fetchData<{ history: NLHistoryEntry[] }>(
        "/insights/nl-history/"
      );
      setState((prev) => ({
        ...prev,
        historyLoading: false,
        history: data.history,
      }));
    } catch (err) {
      logger.error("Failed to fetch NL history:", err);
      setState((prev) => ({ ...prev, historyLoading: false }));
    }
  }, []);

  const clear = useCallback(() => {
    sessionRef.current = undefined;
    setState((prev) => ({ ...prev, conversation: [], error: null }));
  }, []);

  const newConversation = useCallback(() => {
    sessionRef.current = undefined;
    setState((prev) => ({ ...prev, conversation: [], error: null }));
  }, []);

  /** A3: Clear server-side NL query cache, then re-run current question */
  const clearCache = useCallback(async (rerunQuestion?: string) => {
    try {
      await deleteData("/insights/nl-query/cache/");
      logger.info("NL query cache cleared");
      if (rerunQuestion) {
        await queryFn(rerunQuestion);
      }
    } catch (err) {
      logger.error("Failed to clear NL cache:", err);
    }
  }, [queryFn]);

  const latestResult = state.conversation.length > 0
    ? state.conversation[state.conversation.length - 1]
    : null;

  return {
    loading: state.loading,
    result: latestResult,
    conversation: state.conversation,
    error: state.error,
    history: state.history,
    historyLoading: state.historyLoading,
    progressStep: state.progressStep,
    query: queryFn,
    fetchHistory,
    clear,
    clearCache,
    newConversation,
  };
};

export default useNLQuery;
